import { Component, EventEmitter, Input, Output } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-secondary-button',
  templateUrl: './secondary-button.component.html',
  styleUrls: ['./secondary-button.component.scss'],
})
/**
 * Secondary button atom. can be light or dark
 */
export class SecondaryButtonComponent {
  private _bold: boolean = false;
  private _light: boolean = false;
  private _small: boolean = false;
  private _disabled: boolean = false;
  private _auxiliary: boolean = false;
  private _auxiliaryLightBlue: boolean = false;
  private _auxiliaryClean: boolean = false;
  private _auxiliaryGray: boolean = false;
  private _transparent: boolean = false;

  @Input() type?: string;
  @Input() formGroup?: UntypedFormGroup;
  @Input() icon?: string;

  @Output() clickAction = new EventEmitter<any>();

  /**
   * Emits click action event for underlying material button
   */
  emitClick() {
    this.clickAction.emit();
  }

  /**
   * Whether this button is disabled or not
   */
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  /**
   * Sets this button as disabled
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value);
  }

  /**
   * Whether the font-weight is bold or not
   */
  @Input()
  get bold(): boolean {
    return this._bold;
  }

  /**
   * Sets the font-weight as bold
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set bold(value: any) {
    this._bold = coerceBooleanProperty(value);
  }

  /**
   * Whether the color is light or not
   */
  @Input()
  get light(): boolean {
    return this._light;
  }

  /**
   * Sets the color as light
   * @param {any} value value might be any type and it is coerced into a boolean
   */
  set light(value: any) {
    this._light = coerceBooleanProperty(value);
  }

  /**
   * Decides if the component size is small
   * @return {boolean}
   */
  @Input()
  get small(): boolean {
    return this._small;
  }

  /**
   * Sets this component size as small by using an html attribute
   * @param {any} value
   */
  set small(value: any) {
    this._small = coerceBooleanProperty(value);
  }

  /**
   * If this button should be disabled or not
   * @return {boolean}
   */
  isDisabled(): boolean {
    return this.disabled || (Boolean(this.formGroup) && !this.formGroup?.valid);
  }

  /**
   * Decides if the component is auxiliary
   * @return {boolean}
   */
  @Input()
  get auxiliary(): boolean {
    return this._auxiliary;
  }

  /**
   * Sets this component size as auxiliary by using an html attribute
   * @param {any} value
   */
  set auxiliary(value: any) {
    this._auxiliary = coerceBooleanProperty(value);
  }

  /**
   * Decides if the component is auxiliary light blue
   * @return {boolean}
   */
  @Input()
  get auxiliaryLightBlue(): boolean {
    return this._auxiliaryLightBlue;
  }

  /**
   * Sets this component size as auxiliary by using an html attribute
   * @param {any} value
   */
  set auxiliaryLightBlue(value: any) {
    this._auxiliaryLightBlue = coerceBooleanProperty(value);
  }

  /**
   * Decides if the component is auxiliary clean
   * @return {boolean}
   */
  @Input()
  get auxiliaryClean(): boolean {
    return this._auxiliaryClean;
  }

  /**
   * Sets this component size as auxiliary by using an html attribute
   * @param {any} value
   */
  set auxiliaryClean(value: any) {
    this._auxiliaryClean = coerceBooleanProperty(value);
  }

  /**
   * Decides if the component is auxiliary gray
   * @return {boolean}
   */
  @Input()
  get auxiliaryGray(): boolean {
    return this._auxiliaryGray;
  }

  /**
   * Sets this component size as auxiliary by using an html attribute
   * @param {any} value
   */
  set auxiliaryGray(value: any) {
    this._auxiliaryGray = coerceBooleanProperty(value);
  }

  /**
   * Decides if the component is transparent
   * @return {boolean}
   */
  @Input()
  get transparent(): boolean {
    return this._transparent;
  }

  /**
   * Sets this component size as transparent by using an html attribute
   * @param {any} value
   */
  set transparent(value: any) {
    this._transparent = coerceBooleanProperty(value);
  }
}
